import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { QuoteSchema } from "../../../../api/quote-experience/QuoteExperienceProcessor";
import { ButtonGroup, Checkbox, Divider, IconButton, List, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText } from "@mui/material";
import { getByPointer } from "json-pointer-relational";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import './ProductSelector.component.css';
import { Container, Draggable, DropResult } from "react-smooth-dnd";
import { DragHandle } from "@mui/icons-material";

type ProductLine = {
    line: string;
    included: boolean;
    index: number;
}

export const ProductSelector: FC<{
    configSchema: QuoteSchema | undefined;
    configData: Record<string, any>;
    updateData: (pointer: string, value: any, setInitial?: boolean) => void;
}> = ({ configSchema, configData, updateData }) => {
    const [initialStateProcessed, setInitialStateProcessed] = useState<boolean>(false);
    const [productData, setProductData] = useState<Record<string, ProductLine>>({});

    const updateProductData = useCallback((data: Record<string, ProductLine> | ((data: Record<string, ProductLine>) => Record<string, ProductLine>)) => {
        const resolve = (val: typeof data) => {
            if (typeof val === 'function') {
                try { 
                    return val(productData); 
                } catch (err) { 
                    console.log(`Failed to update productData from function callback: ${err}`);
                    return productData; 
                }
            }
            return val;
        };
        const newVal = resolve(data);
        setProductData(newVal);
        updateData(
            '/formDataByType/QUOTE_EXPERIENCE_CONFIG/ORDERED_INSURANCE_LINES',
            Object.values(newVal)
                .filter(product => product.included)
                .sort((a, b) => a.index - b.index)
                .map(product => product.line)
        );
        updateData(
            '/formDataByType/QUOTE_EXPERIENCE_CONFIG/SUPPORTED_INSURANCE_LINES',
            Object.values(newVal)
                .reduce((acc, cur) => {
                    acc[cur.line] = cur.included;
                    return acc;
                }, {} as Record<string, boolean>)
        );
    }, [productData, updateData]);

    useEffect(() => {
        if (initialStateProcessed) {
            return
        }
        setInitialStateProcessed(true);
        const av_ins_lin: string[] = configSchema ? getByPointer('/schema/properties/ORDERED_INSURANCE_LINES/items/enum', configSchema) : [];
        const df_ins_lin: string[] = configSchema ? getByPointer('/schema/properties/ORDERED_INSURANCE_LINES/default', configSchema) : [];
        const saved_ins_lin: string[] = configData?.ORDERED_INSURANCE_LINES || df_ins_lin;
        const productLen = av_ins_lin.length;
        const prod_data =
            av_ins_lin.map(line => {
                const defaultIdx = saved_ins_lin.indexOf(line);
                return {
                    line,
                    included: defaultIdx > -1,
                    index: defaultIdx === -1 ? productLen - 1 : defaultIdx
                }
            })
                .sort((a, b) => a.index - b.index)
                .map((line, index) => ({
                    ...line,
                    index
                })).reduce((acc, cur) => {
                    acc[cur.line] = cur;
                    return acc;
                }, {} as Record<string, ProductLine>);
        console.log('Product Data', JSON.stringify(prod_data));
        updateProductData(prod_data);
    }, [configSchema, configData?.ORDERED_INSURANCE_LINES, initialStateProcessed, updateProductData]);

    const sortedProductLines = useMemo(() =>
        Object.values(productData)
            .sort((a, b) => a.index - b.index)
        , [productData]);

    const swapItems = useCallback((oldIdx: number, newIdx: number) => {
        let items = [...sortedProductLines];
        const movedItem = items[oldIdx];
        items.splice(oldIdx, 1);
        items.splice(newIdx, 0, movedItem);
        updateProductData(items.reduce((acc, cur, idx) => {
            acc[cur.line] = {
                ...cur,
                index: idx
            }
            return acc;
        }, {} as Record<string, ProductLine>));
    }, [sortedProductLines, updateProductData])

    const handleIndexChange = useCallback((product: ProductLine, direction: 'up' | 'down') => {
        const oldIdx = product.index;
        let idx = product.index;
        if (direction === 'down') {
            idx = product.index < sortedProductLines.length - 1 ? product.index + 1 : sortedProductLines.length - 1;
        } else {
            idx = product.index > 0 ? product.index - 1 : 0;
        }
        swapItems(oldIdx, idx);
    }, [swapItems, sortedProductLines]);

    useEffect(() => {
        if (!configData) {
            return;
        }
        updateData(
            '/formDataByType/QUOTE_EXPERIENCE_CONFIG/ORDERED_INSURANCE_LINES',
            Object.values(productData)
                .filter(product => product.included)
                .sort((a, b) => a.index - b.index)
                .map(product => product.line)
        );
        updateData(
            '/formDataByType/QUOTE_EXPERIENCE_CONFIG/SUPPORTED_INSURANCE_LINES',
            Object.values(productData)
                .reduce((acc, cur) => {
                    acc[cur.line] = cur.included;
                    return acc;
                }, {} as Record<string, boolean>)
        );
    }, [configData, productData, updateData])

    const onDrop = useCallback(({ removedIndex, addedIndex }: DropResult) => {
        if (removedIndex === null || addedIndex === null) {
            return
        }
        swapItems(removedIndex, addedIndex);
    }, [swapItems]);

    return (
        <List>
            <Container dragHandleSelector=".drag-handle" lockAxis="y" onDrop={onDrop} >
                {sortedProductLines.map(product => (
                    <Draggable key={`product-list-item-${product.line}`}>
                        <ListItem className="qe-product" >
                            <ListItemIcon>
                                <Checkbox
                                    edge="start"
                                    checked={product.included}
                                    tabIndex={-1}
                                    disableRipple
                                    inputProps={{ 'aria-labelledby': `product-list-item-${product.line}` }}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => updateProductData(prev => ({
                                        ...prev,
                                        [product.line]: {
                                            ...product,
                                            included: checked
                                        }
                                    }))}
                                />

                            </ListItemIcon>
                            <ListItemText className="drag-handle" primary={product.line} />
                            <ListItemSecondaryAction>
                                <ListItemIcon className="drag-handle" >
                                    <DragHandle />
                                </ListItemIcon>
                                <ButtonGroup variant="contained" >
                                    <IconButton
                                        className="qe-shift-button"
                                        onClick={() => handleIndexChange(product, 'up')}
                                    >
                                        <ArrowUpwardIcon />
                                    </IconButton>
                                    <IconButton
                                        className="qe-shift-button"
                                        onClick={() => handleIndexChange(product, 'down')}
                                    >
                                        <ArrowDownwardIcon />
                                    </IconButton>
                                </ButtonGroup>
                            </ListItemSecondaryAction>
                        </ListItem>
                        <Divider />
                    </Draggable>
                ))}
            </Container>
        </List>
    )
}